import React, { useState } from "react";
import { motion } from "framer-motion";
import { ClipboardCheck, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function Assessment() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const bookAndPay = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please fill in name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/assessment/square", {
        name: form.name,
        email: form.email,
      });
      window.alert(
        "✓ Booking received.\n\n" +
        "You'll be sent to Square to complete the $25 assessment fee.\n\n" +
        "AFTER PAYMENT: Davon will reach out within 24 hours to confirm your session time. " +
        "Check your inbox for a confirmation email — keep an eye on spam just in case."
      );
      window.open(data.square_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="assessment"
      className="relative border-t border-zinc-900 py-24 md:py-32 px-6 md:px-12"
      data-testid="assessment-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-4">// Book Your Assessment</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.9]">
            Step 1: <span className="text-[#E5B43A]">$25 Assessment.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base leading-relaxed">
            45-minute movement screen + goal mapping. Pay $25 to lock it in —
            we'll reach out within 24 hours with a confirmed time. The fee credits
            toward your first month.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Booking form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="md:col-span-8 border border-zinc-800 bg-[#141414] p-8 md:p-10"
            data-testid="assessment-form"
          >
            <div className="flex items-center gap-3 mb-6">
              <ClipboardCheck className="text-[#E5B43A]" size={22} />
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-400">Reserve your spot</span>
            </div>
            <p className="text-sm text-zinc-400 mb-8">
              Drop your details, pay the $25 deposit, and Davon will personally
              reach out to schedule a time that fits your week.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Name *"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                className="bg-[#0F0F0F] border border-zinc-800 px-4 py-3 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-[#E5B43A]"
                data-testid="book-name-input"
              />
              <input
                type="email"
                placeholder="Email *"
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                className="bg-[#0F0F0F] border border-zinc-800 px-4 py-3 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-[#E5B43A]"
                data-testid="book-email-input"
              />
            </div>
            <button
              onClick={bookAndPay}
              disabled={loading}
              data-testid="book-pay-button"
              className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-8 py-4 text-sm hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
            >
              {loading ? "Submitting..." : (<>Continue to Square <ArrowRight size={16} /></>)}
            </button>
            <p className="mt-4 text-xs text-zinc-500">
              Secure Square checkout · Refundable up to 24 hours before your scheduled session.
            </p>
          </motion.div>

          {/* Highlights card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="md:col-span-4 border border-[#E5B43A] bg-[#141414] p-8"
            data-testid="assessment-highlights-card"
          >
            <ClipboardCheck className="text-[#E5B43A] mb-4" size={28} />
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-2">What you get</div>
            <ul className="space-y-2 text-sm text-zinc-300 mb-6">
              <li>— 45-min movement & goal session</li>
              <li>— Custom plan recommendation</li>
              <li>— Credited toward your training package</li>
            </ul>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Pay securely through Square once we&apos;ve confirmed your booking. Email
              confirmations are sent automatically.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
